import { Injectable } from '@angular/core';
import { RendererConstants } from '@hi-level/2d-renderer';
import {
  CutoutData,
  FloorRenderingService,
  TransformData,
} from '@iqd/shared-drawing';
import { MD5 } from 'crypto-js';
import { each, find, reject, some } from 'lodash-es';
import { first, Observable, switchMap } from 'rxjs';
import { QuoteData } from './quote-data';
import { CloneQuoteData } from './quote-data-helper';
import { QuoteSessionService } from './quote-session.service';

@Injectable({
  providedIn: 'root',
})
export class TransformComponentsService {
  constructor(
    private quoteSessionService: QuoteSessionService,
    private floorRenderingService: FloorRenderingService
  ) {}

  private get quoteData(): Observable<QuoteData> {
    return this.quoteSessionService.quoteData;
  }

  private removeCutouts(cutouts: any[]): void {
    this.quoteData.pipe(first()).subscribe((quoteData: QuoteData) => {
      const qd = CloneQuoteData(quoteData);
      for (const cutout of cutouts) {
        const tierIndex = cutout.tier.number - 1;
        qd.FloorData.TiersData.TierData[tierIndex].CutoutData = reject(
          qd.FloorData.TiersData.TierData[tierIndex].CutoutData,
          (cutoutData: CutoutData) => cutoutData.Hash === cutout.id
        );
        this.floorRenderingService.renderer.removeCutout(cutout.id, tierIndex);
      }
      qd.TransformData = [];

      this.quoteSessionService.setQuoteData(qd);
    });
  }

  removeComponents(
    components: any[],
    onSuccess: (data: { drawing: string; qd: string }) => void,
    onErrors: (errors: string[]) => void,
    onWarnings: (warnings: string[]) => void
  ): void {
    if (some(components, { name: 'Cutout' })) {
      this.removeCutouts(components);
      return;
    }

    this.quoteData
      .pipe(
        first(),
        switchMap((quoteData) => {
          const qd = CloneQuoteData(quoteData);
          each(components, (component: any, index: number) => {
            const transform = {
              ComponentId: component.id,
              TransformType: 'Remove',
              TierNumber: 0,
              XPosition: 0,
              YPosition: 0,
              Index: qd.TransformData.length + index + 1,
            } as TransformData;
            qd.TransformData.push(transform);
          });
          this.quoteSessionService.setQuoteData(qd, null, true);
          return this.quoteSessionService.onSaved;
        }),
        first()
      )
      .subscribe(() => this.processTransforms(onSuccess, onErrors, onWarnings));
  }

  moveCutouts(cutouts: any[], move: any): void {
    if (!cutouts || !move || !move.value) {
      return;
    }
    let deltaX = 0;
    let deltaY = 0;
    if (move.absolute) {
      deltaX = move.value.x - move.value.startX;
      deltaY = move.value.y - move.value.startY;
    } else {
      deltaX = move.value.right - move.value.left;
      deltaY = move.value.down - move.value.up;
    }
    this.quoteData.pipe(first()).subscribe((quoteData) => {
      let cutoutData = null;
      let tierNumber = 0;
      const qd = CloneQuoteData(quoteData);
      for (const cutout of cutouts) {
        for (let i = 0; i < qd.FloorData.TiersData.TierData.length; i++) {
          const tier = qd.FloorData.TiersData.TierData[i];
          cutoutData = find(tier.CutoutData, { Hash: cutout.id });
          if (cutoutData != null) {
            tierNumber = i + 1;
            break;
          }
        }
        if (cutoutData != null) {
          cutout.x += deltaX * RendererConstants.scalingFactor;
          cutout.y += deltaY * RendererConstants.scalingFactor;
          cutoutData.X += deltaX;
          cutoutData.Y += deltaY;
          const newHash = MD5(
            'Tier:' +
              tierNumber +
              ',X:' +
              cutoutData.X +
              ',Y:' +
              cutoutData.Y +
              ',Z:' +
              0 +
              ',Type:Cutout,XDim:' +
              cutoutData.XDim +
              ',YDim:' +
              cutoutData.YDim +
              ',ZDim:' +
              0
          ).toString();
          cutout.id = newHash;
          cutoutData.Hash = newHash;
        }
      }
      // clear the transforms
      qd.TransformData = [];

      this.quoteSessionService.setQuoteData(qd);
    });
  }

  moveComponents(
    components: any[],
    moveAmounts: any,
    onSuccess: (data: { drawing: string; qd: string }) => void,
    onErrors: (errors: string[]) => void,
    onWarnings: (warnings: string[]) => void
  ): void {
    const deltaX = moveAmounts.right - moveAmounts.left;
    const deltaY = moveAmounts.down - moveAmounts.up;

    this.quoteData
      .pipe(
        first(),
        switchMap((quoteData) => {
          const qd = CloneQuoteData(quoteData);
          if (!qd.TransformData) {
            qd.TransformData = [];
          }
          each(components, (component: any, index: number) => {
            const transform = {
              ComponentId: component.id,
              TransformType: 'Move',
              TierNumber: 0,
              XPosition: deltaX,
              YPosition: deltaY,
              Index: qd.TransformData.length + index + 1,
            } as TransformData;
            qd.TransformData.push(transform);
          });

          this.quoteSessionService.setQuoteData(qd, null, true);
          return this.quoteSessionService.onSaved;
        }),
        first()
      )
      .subscribe(() => this.processTransforms(onSuccess, onErrors, onWarnings));
  }

  private processTransforms(
    onSuccess: (data: { drawing: string; qd: string }) => void,
    onErrors: (errors: string[]) => void,
    onWarnings: (warnings: string[]) => void
  ): void {
    this.quoteSessionService.runJob(
      'generateFloor',
      null,
      (data) => {
        onSuccess(data);
      },
      (errors) => {
        onErrors(errors);
      },
      (warnings) => {
        onWarnings(warnings);
      }
    );
  }
}
